import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  Button,
  useDisclosure,
  useToast,
  Spinner,
  Center,
  Divider,
  Select,
  Badge,
  Collapse,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LuMicroscope, LuActivity, LuShieldCheck, LuUsers, LuChartBar, LuBuilding2, LuArrowDown, LuCircleCheck, LuArrowRight } from 'react-icons/lu'
import PricingCard from '../../components/Subscription/PricingCard'
import EnterpriseFormModal from '../../components/Subscription/EnterpriseFormModal'
import { SubscriptionPlan, PlanType, EnterpriseFormData } from '../../types/subscription'
import { useSelectPlan, useSubmitEnterpriseRequest } from '../../api/api_hooks/subscription'
import { useMyProfile } from '../../api/api_hooks/user/myProfile'
import { useQueryClient } from '@tanstack/react-query'
import { CHROME_HEADER_GRADIENT } from '../../theme/chrome'

export default function PlanSelection() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const queryClient = useQueryClient()

  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [teamSize, setTeamSize] = useState('')
  const [analysisVolume, setAnalysisVolume] = useState('')
  const [establishmentType, setEstablishmentType] = useState('')
  const [showPlans, setShowPlans] = useState(false)
  const [confirmedPlan, setConfirmedPlan] = useState<PlanType | null>(null)
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure()

  const qualificationComplete = teamSize !== '' && analysisVolume !== '' && establishmentType !== ''

  const { data: user, isLoading: isLoadingUser } = useMyProfile()
  const { mutateAsync: selectPlan } = useSelectPlan()
  const { mutateAsync: submitEnterpriseRequest } = useSubmitEnterpriseRequest()

  // If user already has a plan, redirect to home
  useEffect(() => {
    if (!isLoadingUser && user && user.plan) {
      navigate('/home', { replace: true })
    }
  }, [user, isLoadingUser, navigate])

  const PLANS: SubscriptionPlan[] = [
    {
      id: PlanType.PRO,
      name: 'Essentiel',
      price: 690,
      currency: '€',
      billingPeriod: 'month',
      description: 'Workflow assisté IRIS (détection, identifiants, témoin) avec validation manuelle.',
      features: [
        'Un seul opérateur',
        'Modules ALMA et IRIS (immunoblot, validation manuelle)',
        'Détection automatique des bandelettes + reconnaissance des identifiants (OCR)',
        'IRIS — calibration manuelle du témoin positif',
        '200 crédits IA/mois — 1 crédit = 1 image ALMA ou 1 bandelette',
        'Validation opérateur avec traçabilité',
        'Exports PDF, CSV, JSON et FHIR (tracés par identifiant)',
        'Commentaires, annotations, stockage illimité · support prioritaire',
      ],
      ctaText: 'Choisir Essentiel',
      badge: 'Pour démarrer',
    },
    {
      id: PlanType.PREMIUM,
      name: 'Premium',
      price: 990,
      currency: '€',
      billingPeriod: 'month',
      description: 'Interprétation IA automatique pour un gain de temps clinique immédiat.',
      features: [
        'Tout ce qui est inclus dans Essentiel',
        'Collaboratif — plusieurs opérateurs',
        'Crédits IA illimités',
        'IRIS — interprétation IA des immunoblots (verdict par bandelette)',
        'IRIS — calibration automatique du témoin positif',
        'IRIS — comparaison de profils et scoring différentiel témoin/échantillon',
        'Contrôle qualité',
        'Détection automatique ALMA (parasitologie IA)',
        'Partage de cas et téléexpertise collaborative',
        'Aletheia — assistant IA médical intégré',
      ],
      recommended: true,
      ctaText: 'Choisir Premium',
      badge: '✦ IA la plus avancée',
    },
    {
      id: PlanType.ENTERPRISE,
      name: 'Institution',
      price: null,
      currency: '€',
      billingPeriod: null,
      description: 'Solution sur-mesure pour CHU, laboratoires et intégrateurs.',
      features: [
        'Tout ce qui est inclus dans Premium',
        'Intégration SIH (HL7, FHIR, LIMS, DPI)',
        'Déploiement multi-sites et multi-services',
        'Développement de modules spécifiques',
        'Conformité HDS et accompagnement RGPD',
        'Gestionnaire de compte dédié',
        'SLA personnalisé et formation équipe',
        'Tarification au volume',
      ],
      ctaText: "Contacter l'équipe",
      badge: 'Sur devis',
    },
  ]

  const defaultModules = (planId: PlanType) =>
    planId === PlanType.PREMIUM || planId === PlanType.ENTERPRISE
      ? ['lab', 'teleexpertise', 'medinsight']
      : ['lab']

  const handlePlanSelect = async (planId: PlanType) => {
    if (planId === PlanType.ENTERPRISE) { onOpen(); return }
    setIsProcessing(true)
    setSelectedPlan(planId)
    try {
      const modules = defaultModules(planId)
      await selectPlan({
        plan: planId,
        modules,
        meta: { establishmentType, teamSize, analysisVolume },
      })
      localStorage.setItem('userPlan', planId)
      localStorage.setItem('userModules', JSON.stringify(modules))
      await queryClient.refetchQueries()
      setConfirmedPlan(planId)
      onConfirmOpen()
    } catch (_e) {
      toast({ title: 'Échec de la sélection', status: 'error', duration: 5000 })
      setSelectedPlan(null)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleEnterpriseSubmit = async (formData: EnterpriseFormData) => {
    await submitEnterpriseRequest({ ...formData, modules: defaultModules(PlanType.ENTERPRISE) })
    setSelectedPlan(PlanType.ENTERPRISE_PENDING)
    localStorage.setItem('userPlan', PlanType.ENTERPRISE_PENDING)
    await queryClient.refetchQueries()
    setTimeout(() => navigate('/home'), 500)
  }

  if (isLoadingUser) {
    return <Center h="100vh" bg="gray.50"><Spinner size="xl" color="brand.purple" thickness="4px" /></Center>
  }

  const firstName = user?.contactName?.split(' ')[0] ?? ''

  const VALUE_PROPS = [
    {
      icon: LuMicroscope,
      tag: 'ALMA · IA',
      title: 'Analyse parasitologique',
      desc: 'Détectez et labellisez les hématies parasitées sur vos frottis, manuellement ou avec l\'assistance IA.',
      stat: null, statLabel: null,
      bg: 'linear-gradient(135deg, #3b0764 0%, #6d28d9 60%, #a855f7 100%)',
    },
    {
      icon: LuActivity,
      tag: 'IRIS · IA',
      title: 'Immunoblot assisté (IRIS)',
      desc: 'Comparez vos profils immunoblot au témoin positif et validez en un clic.',
      stat: null, statLabel: null,
      bg: 'linear-gradient(135deg, #064e3b 0%, #0f766e 60%, #14b8a6 100%)',
    },
    {
      icon: LuShieldCheck,
      tag: 'Traçabilité',
      title: 'Conçu pour les équipes',
      desc: 'Traçabilité opérateur horodatée, export PDF structuré et gestion des dossiers patients.',
      stat: null, statLabel: null,
      bg: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 60%, #60a5fa 100%)',
    },
  ]

  // Recommandation selon les réponses
  const getRecommendedPlan = () => {
    if (!teamSize || !analysisVolume) return null
    const highVolume = ['50-200', '200+'].includes(analysisVolume)
    const bigTeam = ['6-10', '10+'].includes(teamSize)
    if (bigTeam || highVolume) return PlanType.ENTERPRISE
    if (['10-50', '50-200'].includes(analysisVolume)) return PlanType.PREMIUM
    return PlanType.PRO
  }
  const recommended = getRecommendedPlan()

  return (
    <Box minH="100vh" h="100vh" bg="gray.50" overflowY="auto">

      {/* Hero */}
      <Box
        bgGradient={CHROME_HEADER_GRADIENT}
        px={6} py={{ base: 10, md: 14 }}
        textAlign="center" position="relative" overflow="hidden"
      >
        <Box position="absolute" top="-80px" right="-60px" w="300px" h="300px" borderRadius="full"
          bg="radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)" pointerEvents="none" />
        <Box position="absolute" bottom="-60px" left="-40px" w="200px" h="200px" borderRadius="full"
          bg="radial-gradient(circle, rgba(219,39,119,0.2) 0%, transparent 70%)" pointerEvents="none" />
        <VStack spacing={3} position="relative" zIndex={1}>
          <Text fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" letterSpacing="0.1em" textTransform="uppercase">
            Careflai · Onboarding
          </Text>
          <Heading size={{ base: 'xl', md: '2xl' }} color="white" fontWeight="800" letterSpacing="-0.02em" lineHeight="1.15">
            {firstName ? `Bonjour Dr. ${firstName} 👋` : 'Bienvenue sur Careflai 👋'}
          </Heading>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="whiteAlpha.800" maxW="460px" lineHeight="1.7">
            Votre espace d'analyse médicale est prêt. Voici ce que vous pouvez faire dès aujourd'hui.
          </Text>
        </VStack>
      </Box>

      {/* Value props */}
      <Box maxW="900px" mx="auto" px={6} py={8}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {VALUE_PROPS.map(({ icon, tag, title, desc, stat, statLabel, bg }) => (
            <Box
              key={title}
              borderRadius="2xl"
              overflow="hidden"
              position="relative"
              boxShadow="0 8px 32px rgba(0,0,0,0.18)"
              _hover={{ transform: 'translateY(-4px)', boxShadow: '0 16px 48px rgba(0,0,0,0.22)' }}
              transition="all 0.25s"
              cursor="default"
            >
              {/* Full gradient background */}
              <Box
                position="absolute" inset={0}
                style={{ background: bg }}
              />
              {/* Decorative blob */}
              <Box
                position="absolute" bottom="-30px" right="-30px"
                w="120px" h="120px" borderRadius="full"
                bg="rgba(255,255,255,0.08)" pointerEvents="none"
              />

              <Box position="relative" zIndex={1} p={6}>
                {/* Tag */}
                <Box
                  display="inline-flex" alignItems="center"
                  bg="rgba(255,255,255,0.15)" borderRadius="full"
                  px={2.5} py={0.5} mb={4}
                >
                  <Text fontSize="2xs" color="white" fontWeight="700" letterSpacing="0.08em">
                    {tag}
                  </Text>
                </Box>

                {/* Icon */}
                <Box
                  w="44px" h="44px" borderRadius="xl"
                  bg="rgba(255,255,255,0.2)"
                  display="flex" alignItems="center" justifyContent="center"
                  mb={4}
                >
                  <Icon as={icon} boxSize={5} color="white" />
                </Box>

                {/* Title */}
                <Text fontWeight="800" fontSize="md" color="white" lineHeight="1.2" mb={2}>
                  {title}
                </Text>

                {/* Description */}
                <Text fontSize="xs" color="rgba(255,255,255,0.75)" lineHeight="1.65" mb={5}>
                  {desc}
                </Text>

                {/* Stat — optionnel */}
                {stat && (
                  <Box borderTop="1px solid" borderColor="rgba(255,255,255,0.2)" pt={3}>
                    <HStack spacing={2} align="baseline">
                      <Text fontSize="xl" fontWeight="800" color="white" lineHeight="1">{stat}</Text>
                      <Text fontSize="2xs" color="rgba(255,255,255,0.6)" fontWeight="500">{statLabel}</Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Qualification form */}
      <Box maxW="860px" mx="auto" px={6} pb={6}>
        <Box
          bg="white" borderRadius="2xl" border="1px solid"
          borderColor={qualificationComplete ? 'purple.200' : 'gray.100'}
          boxShadow={qualificationComplete ? '0 0 0 3px rgba(124,58,237,0.08)' : 'sm'}
          p={7} transition="all 0.3s"
        >
          <HStack justify="space-between" mb={1} align="start">
            <VStack align="start" spacing={0}>
              <Text fontWeight="700" fontSize="md" color="gray.800">
                Quelques infos pour personnaliser votre accès
              </Text>
              <Text fontSize="xs" color="gray.400" mt={0.5}>
                Remplissez les 3 champs ci-dessous pour voir les offres adaptées à votre profil.
              </Text>
            </VStack>
            {qualificationComplete && (
              <Badge colorScheme="green" borderRadius="full" px={3} py={1} fontSize="2xs" flexShrink={0}>
                ✓ Complété
              </Badge>
            )}
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={5}>
            {/* Type d'établissement */}
            <Box>
              <HStack spacing={1.5} mb={2}>
                <Icon as={LuBuilding2} boxSize={3.5} color="purple.500" />
                <Text fontSize="xs" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.05em">
                  Type d'établissement
                </Text>
              </HStack>
              <Select
                placeholder="Sélectionner"
                size="sm" borderRadius="lg" bg="gray.50"
                borderColor={establishmentType ? 'purple.300' : 'gray.200'}
                value={establishmentType}
                onChange={(e) => setEstablishmentType(e.target.value)}
              >
                <option value="chu">CHU / Hôpital public</option>
                <option value="clinique">Clinique privée</option>
                <option value="labo">Laboratoire d'analyses</option>
                <option value="recherche">Centre de recherche</option>
                <option value="autre">Autre</option>
              </Select>
            </Box>

            {/* Taille équipe */}
            <Box>
              <HStack spacing={1.5} mb={2}>
                <Icon as={LuUsers} boxSize={3.5} color="purple.500" />
                <Text fontSize="xs" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.05em">
                  Taille de l'équipe
                </Text>
              </HStack>
              <Select
                placeholder="Sélectionner"
                size="sm" borderRadius="lg" bg="gray.50"
                borderColor={teamSize ? 'purple.300' : 'gray.200'}
                value={teamSize}
                onChange={(e) => setTeamSize(e.target.value)}
              >
                <option value="1">1 professionnel</option>
                <option value="2-5">2 – 5 professionnels</option>
                <option value="6-10">6 – 10 professionnels</option>
                <option value="10+">Plus de 10</option>
              </Select>
            </Box>

            {/* Volume analyses */}
            <Box>
              <HStack spacing={1.5} mb={2}>
                <Icon as={LuChartBar} boxSize={3.5} color="purple.500" />
                <Text fontSize="xs" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.05em">
                  Analyses / mois
                </Text>
              </HStack>
              <Select
                placeholder="Sélectionner"
                size="sm" borderRadius="lg" bg="gray.50"
                borderColor={analysisVolume ? 'purple.300' : 'gray.200'}
                value={analysisVolume}
                onChange={(e) => setAnalysisVolume(e.target.value)}
              >
                <option value="<10">Moins de 10</option>
                <option value="10-50">10 – 50</option>
                <option value="50-200">50 – 200</option>
                <option value="200+">Plus de 200</option>
              </Select>
            </Box>
          </SimpleGrid>

          {/* Résultat + CTA */}
          {qualificationComplete && (
            <Box mt={5}>
              <Box p={4} bg="purple.50" borderRadius="xl" border="1px solid" borderColor="purple.100" mb={4}>
                <HStack spacing={2}>
                  <Text fontSize="lg">💡</Text>
                  <Text fontSize="sm" color="purple.800" lineHeight="1.5">
                    D'après votre profil, l'offre{' '}
                    <Text as="span" fontWeight="800">
                      {recommended === PlanType.PRO ? 'Essentiel' : recommended === PlanType.PREMIUM ? 'Premium' : 'Institution'}
                    </Text>
                    {' '}semble la plus adaptée.{' '}
                    <Text as="span" color="purple.600">Découvrez les détails ci-dessous.</Text>
                  </Text>
                </HStack>
              </Box>
              {!showPlans && (
                <Button
                  w="100%"
                  bgGradient="linear(to-r, purple.600, pink.500)"
                  color="white"
                  borderRadius="xl"
                  size="md"
                  rightIcon={<Icon as={LuArrowDown} />}
                  _hover={{ bgGradient: 'linear(to-r, purple.700, pink.600)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  transition="all 0.2s"
                  onClick={() => setShowPlans(true)}
                >
                  Voir les offres
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Plans — revealed after qualification */}
      <Collapse in={showPlans} animateOpacity>
        <Box>
          <Box maxW="860px" mx="auto" px={6} pb={5}>
            <HStack spacing={4} align="center">
              <Divider />
              <Text fontSize="2xs" color="gray.400" whiteSpace="nowrap" fontWeight="700" letterSpacing="0.1em" textTransform="uppercase">
                Choisissez votre accès
              </Text>
              <Divider />
            </HStack>
          </Box>

          <Box maxW="1100px" mx="auto" px={6} pb={14}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} alignItems="start">
              {PLANS.map((plan) => (
                <Box key={plan.id} position="relative">
                  {recommended === plan.id && (
                    <Badge
                      position="absolute" top="-10px" left="50%" transform="translateX(-50%)"
                      bg="purple.600" color="white" borderRadius="full" px={3} py={1}
                      fontSize="2xs" fontWeight="bold" letterSpacing="0.06em" zIndex={2} boxShadow="md"
                    >
                      RECOMMANDÉ
                    </Badge>
                  )}
                  <PricingCard
                    plan={plan}
                    isSelected={selectedPlan === plan.id}
                    onSelect={() => handlePlanSelect(plan.id)}
                    isLoading={isProcessing && selectedPlan === plan.id}
                  />
                </Box>
              ))}
            </SimpleGrid>
            <Text fontSize="xs" color="gray.400" textAlign="center" mt={8}>
              Tous les prix sont HT · par professionnel · résiliable à tout moment
            </Text>
          </Box>
        </Box>
      </Collapse>

      {/* Confirmation modal */}
      <Modal isOpen={isConfirmOpen} onClose={() => {}} isCentered size="md" closeOnOverlayClick={false}>
        <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.500" />
        <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="0 24px 80px rgba(0,0,0,0.2)">
          {/* Gradient header */}
          <Box
            bgGradient={CHROME_HEADER_GRADIENT}
            px={8} py={8} textAlign="center" position="relative" overflow="hidden"
          >
            <Box position="absolute" top="-40px" right="-40px" w="150px" h="150px" borderRadius="full"
              bg="radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)" pointerEvents="none" />
            <Box
              w="64px" h="64px" bg="rgba(255,255,255,0.2)" borderRadius="full"
              display="flex" alignItems="center" justifyContent="center" mx="auto" mb={4}
              boxShadow="0 0 0 8px rgba(255,255,255,0.1)"
            >
              <Icon as={LuCircleCheck} boxSize={8} color="white" />
            </Box>
            <Heading size="lg" color="white" fontWeight="800" mb={1}>
              Votre offre est activée 🎉
            </Heading>
            <Text fontSize="sm" color="whiteAlpha.800">
              {confirmedPlan === PlanType.PRO ? 'Essentiel — 690 €/mois' :
               confirmedPlan === PlanType.PREMIUM ? 'Premium — 990 €/mois' : 'Offre activée'}
            </Text>
          </Box>

          <ModalBody px={8} py={7}>
            <VStack spacing={5} align="stretch">

              {/* What's included */}
              <Box bg="gray.50" borderRadius="xl" p={4}>
                <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                  Ce qui est inclus
                </Text>
                <VStack spacing={2} align="stretch">
                  {(confirmedPlan === PlanType.PREMIUM ? [
                    'Collaboratif — plusieurs opérateurs',
                    'Détection IA automatique ALMA',
                    'IRIS — interprétation IA des immunoblots',
                    'IRIS — calibration automatique du témoin positif',
                    'Contrôle qualité',
                    'Aletheia — assistant IA médical',
                    'Crédits IA illimités',
                    'Partage de cas et téléexpertise',
                  ] : [
                    'Un seul opérateur',
                    'Modules ALMA et IRIS (immunoblot, validation manuelle)',
                    'Détection des bandelettes + identifiants (OCR)',
                    'IRIS — calibration manuelle du témoin positif',
                    '200 crédits IA / mois (1 crédit par image ou par bandelette)',
                    'Exports PDF, CSV, JSON et FHIR',
                    'Support prioritaire',
                  ]).map((item) => (
                    <HStack key={item} spacing={2}>
                      <Icon as={LuCircleCheck} boxSize={3.5} color="purple.500" flexShrink={0} />
                      <Text fontSize="sm" color="gray.700">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {/* 48h message */}
              <Box bg="amber.50" border="1px solid" borderColor="orange.100" borderRadius="xl" p={4}
                style={{ background: '#fffbeb' }}>
                <HStack spacing={3} align="start">
                  <Text fontSize="xl" mt={0.5}>⏱</Text>
                  <VStack align="start" spacing={0.5}>
                    <Text fontSize="sm" fontWeight="700" color="gray.800">
                      Règlement à finaliser
                    </Text>
                    <Text fontSize="xs" color="gray.500" lineHeight="1.5">
                      Notre équipe vous contactera sous <Text as="span" fontWeight="700" color="orange.600">48h</Text> pour finaliser le règlement adapté à votre établissement.
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              {/* CTA */}
              <Button
                w="100%"
                size="lg"
                bgGradient="linear(to-r, purple.600, pink.500)"
                color="white"
                borderRadius="xl"
                fontWeight="700"
                rightIcon={<Icon as={LuArrowRight} />}
                _hover={{ bgGradient: 'linear(to-r, purple.700, pink.600)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                transition="all 0.2s"
                onClick={() => { onConfirmClose(); navigate('/home') }}
              >
                Accéder à Careflai
              </Button>

              <Text fontSize="2xs" color="gray.400" textAlign="center">
                Vous pouvez changer d'offre à tout moment depuis votre profil.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <EnterpriseFormModal isOpen={isOpen} onClose={onClose} onSubmit={handleEnterpriseSubmit} />
    </Box>
  )
}
